/**
 * Centralized Notification Routing Utility
 * 
 * This utility provides consistent routing for all notification types across the app.
 * It handles navigation based on notification type and extracted data.
 */

import { USER_TYPES } from '../constants/userTypes';

/**
 * Get clinic ID from various sources
 */
const getClinicId = () => {
  try {
    // Try to get from URL params
    const pathMatch = window.location.pathname.match(/\/medical\/clinic\/(\d+)/);
    if (pathMatch) return pathMatch[1];

    // Try to get from localStorage
    const userData = localStorage.getItem('user_data');
    if (userData && userData !== 'undefined' && userData !== 'null') {
      const parsed = JSON.parse(userData);
      if (parsed.clinic_id) return parsed.clinic_id;
    }
  } catch (e) {
    // Ignore errors
  }
  return null;
};

/**
 * Get current user ID from localStorage
 */
const getCurrentUserId = () => {
  try {
    const userData = localStorage.getItem('user_data');
    if (userData && userData !== 'undefined' && userData !== 'null') {
      const parsed = JSON.parse(userData);
      return parsed.id || parsed.user_id || null;
    }
  } catch (e) {
    // Ignore errors
  }
  return null;
};

/**
 * Get user type from localStorage
 */
const getUserType = () => {
  try {
    const userData = localStorage.getItem('user_data');
    if (userData && userData !== 'undefined' && userData !== 'null') {
      const parsed = JSON.parse(userData);
      return parsed.user_type || parsed.userType;
    }
  } catch (e) {
    // Ignore errors
  }
  return null;
};

/**
 * Centralized notification routing handler
 * 
 * @param {Object} notification - The notification object
 * @param {Function} navigate - React Router navigate function
 * @param {Function} onClose - Optional callback to close dropdown/modal
 * @param {Function} openPostModal - Optional callback to open post modal
 * @returns {boolean} True if routing was handled, false otherwise
 */
export const handleNotificationRouting = (notification, navigate, onClose = null, openPostModal = null) => {
  if (!notification) return false;

  const notificationType = notification.type;
  const parsedData = notification.parsedData || notification.data_decoded || {};
  const userType = getUserType();
  const currentUserId = getCurrentUserId();

  // Recipient is the pet guardian when their id matches the visit's pet owner
  const isPetOwnerRecipient = parsedData.pet_owner_id && currentUserId &&
    String(parsedData.pet_owner_id) === String(currentUserId);

  // Route a pet guardian to their dashboard's past visits for this pet
  const routeGuardianToPastVisits = (visitId, visitType) => {
    if (visitId) {
      sessionStorage.setItem('highlightPastVisitId', visitId.toString());
      sessionStorage.setItem('highlightPastVisitType', visitType);
    } else {
      sessionStorage.removeItem('highlightPastVisitId');
      sessionStorage.removeItem('highlightPastVisitType');
    }
    const petId = parsedData.pet_id || notification.target_id;
    const dashboardUrl = userType === USER_TYPES.SERVICE_PROVIDER
      ? '/medical/dashboard/petshopowner/overview'
      : '/medical/dashboard/petguardian/overview';
    navigate(`${dashboardUrl}?petId=${petId}&recordType=past-visits`);
    if (onClose) onClose();
    return true;
  };

  // Check if this is a channel notification, reaction, comment, or post-deleted that uses handleClick
  const isChannelNotification = (type) => type?.startsWith("channel_");

  if (notification.handleClick && (
    isChannelNotification(notificationType) ||
    notificationType === "reaction" ||
    notificationType === "post_reaction" ||
    notificationType === "story_reaction" ||
    notificationType === "comment" ||
    notificationType === "comment_reply" ||
    notificationType === "repost" ||
    notificationType === "post_deleted_by_admin"
  )) {
    // Use the notification's built-in handleClick
    notification.handleClick(navigate, onClose || (() => { }), openPostModal || (() => { }));
    return true;
  }

  // Don't handle click for follow requests (they have their own buttons)
  if (notificationType === "new_follow_request" || notificationType === "connection_request" || notificationType === "guardianship_invitation") {
    return false;
  }

  // Extract common IDs
  const appointmentId = parsedData.appointment_id || notification.target_id;
  const outpatientVisitId = parsedData.outpatient_visit_id || parsedData.visit_id;
  const inpatientVisitId = parsedData.inpatient_visit_id;
  const adoptionRequestId = parsedData.adoption_request_id || parsedData.request_id || notification.target_id;
  const urgentCaseId = parsedData.urgent_case_id || parsedData.case_id || notification.content_id || notification.target_id;
  const postId = parsedData.post_id || parsedData.content_id || notification.post_id || notification.content_id || notification.target_id;
  const userId = parsedData.user_id || parsedData.sender_id || notification.sender_id || notification.target_id;
  const clinicId = parsedData.clinic_id || getClinicId();
  const senderType = parsedData.sender_type || notification.sender_type || null;
  const senderClinicId = parsedData.clinic_id || parsedData.clinicId || notification.clinic_id || null;
  const actionUrl = parsedData.action_url || notification.action_url;

  // Route based on notification type
  switch (notificationType) {
    // Appointment notifications
    case "appointment_created":
    case "appointment_updated":
    case "appointment_canceled":
    case "appointment_restored":
    case "appointment_status_changed":
      // For pet guardians and service providers, navigate to their dashboard appointments tab
      if (userType === USER_TYPES.PET_GUARDIAN || userType === USER_TYPES.SERVICE_PROVIDER) {
        if (appointmentId) {
          sessionStorage.setItem('highlightAppointmentId', appointmentId.toString());
        }
        const dashboardUrl = userType === USER_TYPES.SERVICE_PROVIDER
          ? '/medical/dashboard/petshopowner?tab=appointments'
          : '/medical/dashboard/petguardian?tab=appointments';
        navigate(dashboardUrl);
        if (onClose) onClose();
        return true;
      }
      // For veterinarians and clinic staff, navigate to reception appointments
      else if (userType === USER_TYPES.VETERINARIAN || userType === USER_TYPES.CLINIC_MODERATOR) {
        // Set highlight ID if available, otherwise clear it
        if (appointmentId) {
          sessionStorage.setItem('highlightAppointmentId', appointmentId.toString());
        } else {
          sessionStorage.setItem('highlightAppointmentId', '');
        }

        // Navigate to clinic appointment management
        const path = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/appointments`
          : '/medical/clinic/redirect-to-clinic';

        navigate(path);
        if (onClose) onClose();
        return true;
      }
      // Fallback for other user types or if userType is not available
      else if (appointmentId) {
        sessionStorage.setItem('highlightAppointmentId', appointmentId.toString());
        const path = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/appointments`
          : '/medical/clinic/redirect-to-clinic';
        navigate(path);
        if (onClose) onClose();
        return true;
      }
      break;

    // Outpatient visit notifications
    case "outpatient_visit_created":
    case "outpatient_visit_updated":
    case "outpatient_visit_status_changed":
      // Pet guardians go to their own dashboard's past visits, not the clinic dashboard
      if ((userType === USER_TYPES.PET_GUARDIAN || isPetOwnerRecipient) && parsedData.pet_id) {
        return routeGuardianToPastVisits(outpatientVisitId, 'outpatient');
      }
      if (outpatientVisitId) {
        sessionStorage.setItem('highlightOutpatientVisitId', outpatientVisitId.toString());
        const path = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/outpatient`
          : '/medical/clinic/redirect-to-clinic';
        navigate(path);
        if (onClose) onClose();
        return true;
      }
      break;

    // Inpatient visit notifications
    case "inpatient_visit_created":
    case "inpatient_visit_updated":
    case "inpatient_visit_status_changed":
      // Pet guardians go to their own dashboard's past visits, not the clinic dashboard
      if ((userType === USER_TYPES.PET_GUARDIAN || isPetOwnerRecipient) && parsedData.pet_id) {
        return routeGuardianToPastVisits(inpatientVisitId, 'inpatient');
      }
      if (inpatientVisitId) {
        sessionStorage.setItem('highlightInpatientVisitId', inpatientVisitId.toString());
        const path = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/inpatient`
          : '/medical/clinic/redirect-to-clinic';
        navigate(path);
        if (onClose) onClose();
        return true;
      }
      break;

    // Adoption notifications
    case "adoption_request_received":
      if (adoptionRequestId) {
        navigate(`/adoption?openRequests=true&highlightRequest=${adoptionRequestId}`);
      } else {
        navigate('/adoption?openRequests=true');
      }
      if (onClose) onClose();
      return true;

    case "adoption_request_accepted":
    case "adoption_request_rejected":
      navigate('/adoption');
      if (onClose) onClose();
      return true;

    // Message/Chat notifications
    case "message":
    case "chat":
    case "new_message":
      if (actionUrl) {
        navigate(actionUrl);
        if (onClose) onClose();
        return true;
      } else if (notification.content_id || notification.user_id || userId) {
        const chatId = notification.content_id || notification.user_id || userId;
        navigate(`/chat/${chatId}`);
        if (onClose) onClose();
        return true;
      } else {
        navigate('/chat');
        if (onClose) onClose();
        return true;
      }

    // Conversation closed notifications (e.g., when SOS conversation is closed after appointment scheduling)
    case "conversation_closed":
      // Extract conversation ID from various possible locations
      const conversationId = parsedData.conversation_id ||
        notification.content_id ||
        notification.target_id ||
        notification.conversationId ||
        (actionUrl ? parseInt(actionUrl.split('/chat/')[1]?.split('/')[0]) : null);

      if (conversationId) {
        // Navigate to the closed conversation to show closure status
        navigate(`/chat/${conversationId}`);
        if (onClose) onClose();
        return true;
      } else if (actionUrl) {
        navigate(actionUrl);
        if (onClose) onClose();
        return true;
      } else {
        // Fallback to chat list
        navigate('/chat');
        if (onClose) onClose();
        return true;
      }

    // New user in same city
    case "new_user_same_city":
      // When the sender is a clinic, route to the clinic page instead of a user profile
      if (senderType === 'clinic' && senderClinicId) {
        navigate(`/clinic/${senderClinicId}`);
        if (onClose) onClose();
        return true;
      }
      if (userId) {
        navigate(`/profile/${userId}`);
        if (onClose) onClose();
        return true;
      }
      break;

    // Urgent case notifications
    case "urgent_case":
    case "urgent_case_created":
    case "urgent_case_updated":
      if (urgentCaseId) {
        // Navigate to urgent case details or emergency page
        navigate(`/medical/urgent-case/${urgentCaseId}`);
        if (onClose) onClose();
        return true;
      } else if (actionUrl) {
        navigate(actionUrl);
        if (onClose) onClose();
        return true;
      } else {
        // Fallback to emergency page
        navigate('/emergency');
        if (onClose) onClose();
        return true;
      }

    // Clinic invitation notifications
    case "clinic_invitation":
      // Navigate to clinic management or invitations page
      if (clinicId) {
        navigate(`/medical/clinic/${clinicId}/manage-team`);
        if (onClose) onClose();
        return true;
      } else {
        navigate('/medical/manage-team');
        if (onClose) onClose();
        return true;
      }

    // Post-related notifications (if not handled by handleClick)
    case "new_post":
    case "post_created":
    case "post_updated":
    case "post_deleted":
      if (postId && openPostModal) {
        openPostModal(postId);
        if (onClose) onClose();
        return true;
      } else if (postId) {
        navigate(`/social/post/${postId}`);
        if (onClose) onClose();
        return true;
      }
      break;

    // Video processing notifications
    case "video_processing_completed":
    case "video_processing_failed":
      if (postId && openPostModal) {
        openPostModal(postId);
        if (onClose) onClose();
        return true;
      } else if (postId) {
        navigate(`/social/post/${postId}`);
        if (onClose) onClose();
        return true;
      }
      break;

    // Pet-related notifications
    case "pet_added":
    case "pet_updated":
    case "guardianship_invitation":
    case "guardianship_response":
      const petId = parsedData.pet_id || notification.target_id;
      if (petId) {
        navigate(`/pets/${petId}/view`);
        if (onClose) onClose();
        return true;
      } else {
        navigate('/dashboard/pets');
        if (onClose) onClose();
        return true;
      }

    // Medical record notifications
    case "medical_record_created":
    case "medical_record_updated":
      const recordId = parsedData.record_id || notification.target_id;
      const petIdForRecord = parsedData.pet_id;
      if (recordId && petIdForRecord) {
        navigate(`/pets/${petIdForRecord}/view?record=${recordId}`);
        if (onClose) onClose();
        return true;
      } else if (petIdForRecord) {
        navigate(`/pets/${petIdForRecord}/view`);
        if (onClose) onClose();
        return true;
      }
      break;

    // Health reminder notifications (vaccinations, deworming, etc.)
    case "health_reminder":
    case "vaccination_reminder":
    case "deworming_reminder": {
      // Try to extract petId from action_url or parsed data
      let reminderPetId = parsedData.pet_id || notification.target_id;
      let reminderRecordType = parsedData.record_type || parsedData.recordType;

      // Parse action_url query params if petId not directly available
      if (!reminderPetId && actionUrl) {
        try {
          const url = new URL(actionUrl, window.location.origin);
          const urlParams = new URLSearchParams(url.search);
          reminderPetId = urlParams.get('petId') || urlParams.get('pet_id');
          reminderRecordType = reminderRecordType || urlParams.get('recordType') || urlParams.get('record_type');
        } catch (e) {
          // Ignore URL parse errors
        }
      }

      // Normalize backend recordType values to pet profile subTab keys
      const recordTypeMap = {
        'vaccinations': 'vaccination',
        'medications': 'medication',
        'allergies': 'allergy',
        'past-illness-history': 'pastIllness',
        'surgeries': 'surgery',
        'dental-records': 'dental',
        'deworming': 'dewormingAntiFlea',
      };
      const normalizedRecordType = recordTypeMap[reminderRecordType] || reminderRecordType;

      if (reminderPetId) {
        if (userType === USER_TYPES.PET_GUARDIAN) {
          // Pet guardians go to their care dashboard
          const guardianUrl = `/medical/dashboard/petguardian/overview?petId=${reminderPetId}${reminderRecordType ? `&recordType=${reminderRecordType}` : ''}`;
          navigate(guardianUrl);
        } else if (userType === USER_TYPES.VETERINARIAN || userType === USER_TYPES.CLINIC_MODERATOR) {
          // Vets/clinic staff go to the pet's medical record page
          const petProfileUrl = `/pets/${reminderPetId}/view/medicalRecord${normalizedRecordType ? `?subTab=${normalizedRecordType}` : ''}`;
          navigate(petProfileUrl);
        } else {
          // Fallback for other user types
          navigate(`/pets/${reminderPetId}/view`);
        }
        if (onClose) onClose();
        return true;
      } else if (actionUrl) {
        // No petId extracted, fall back to action_url
        if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
          window.location.href = actionUrl;
        } else {
          navigate(actionUrl);
        }
        if (onClose) onClose();
        return true;
      }
      break;
    }

    // Marketplace notifications
    case "marketplace_order_created":
    case "marketplace_order_updated":
    case "marketplace_order_canceled":
      const orderId = parsedData.order_id || notification.target_id;
      if (orderId) {
        navigate(`/marketplace/orders/${orderId}`);
        if (onClose) onClose();
        return true;
      } else {
        navigate('/marketplace/orders');
        if (onClose) onClose();
        return true;
      }

    // Default fallback - use action_url if available
    default:
      if (actionUrl) {
        // Check if it's a relative URL or absolute
        if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
          window.location.href = actionUrl;
        } else {
          navigate(actionUrl);
        }
        if (onClose) onClose();
        return true;
      }
      // If no action_url and no specific handler, don't navigate
      return false;
  }

  // If we have an actionUrl as fallback
  if (actionUrl) {
    if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
      window.location.href = actionUrl;
    } else {
      navigate(actionUrl);
    }
    if (onClose) onClose();
    return true;
  }

  return false;
};

/**
 * Check if a notification type should be clickable
 * Some notifications have their own action buttons and shouldn't navigate on click
 */
export const isNotificationClickable = (notificationType) => {
  // These types have their own action buttons and shouldn't navigate on click
  // (users should use the action buttons instead)
  const nonClickableTypes = [
    "new_follow_request",
    "connection_request",
    "guardianship_invitation",
    "urgent_case_closed"
    // Note: clinic_invitation is clickable (can navigate to manage-team)
    // even though it has action buttons
  ];

  return !nonClickableTypes.includes(notificationType);
};

/**
 * Get a human-readable route description for a notification
 * Useful for debugging or showing route info
 */
export const getNotificationRouteDescription = (notification) => {
  const notificationType = notification?.type;
  const parsedData = notification?.parsedData || {};

  switch (notificationType) {
    case "appointment_created":
    case "appointment_updated":
      return `Navigate to appointments dashboard`;
    case "adoption_request_received":
      return `Navigate to adoption page with request highlighted`;
    case "message":
    case "new_message":
      return `Navigate to chat`;
    case "urgent_case":
      return `Navigate to urgent case details`;
    default:
      return `Navigate based on notification type: ${notificationType}`;
  }
};
